import React, { createContext, useContext, useState, useCallback } from 'react';
import meTranslations from './me.json';
import sqTranslations from './sq.json';

const translations = {
  me: meTranslations,
  sq: sqTranslations,
};

const STORAGE_KEY = 'vik-language';
const DEFAULT_LANG = 'me';

function getStoredLanguage() {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getStoredLanguage);

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
  }, []);

  const t = useCallback((key, params) => {
    const dict = translations[language] || translations[DEFAULT_LANG];
    let str = dict[key] || translations[DEFAULT_LANG][key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      });
    }
    return str;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback if used outside provider (e.g. portal page)
    return {
      language: DEFAULT_LANG,
      setLanguage: () => {},
      t: (key, params) => {
        let str = translations[DEFAULT_LANG][key] || key;
        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
          });
        }
        return str;
      },
    };
  }
  return ctx;
}

// Simple language switcher toggle component
export function LanguageSwitcher() {
  const { language, setLanguage } = useTranslation();

  return (
    <div style={switcherStyles.container}>
      <button
        onClick={() => setLanguage('me')}
        style={{
          ...switcherStyles.btn,
          ...(language === 'me' ? switcherStyles.active : {}),
        }}
      >
        ME
      </button>
      <button
        onClick={() => setLanguage('sq')}
        style={{
          ...switcherStyles.btn,
          ...(language === 'sq' ? switcherStyles.active : {}),
        }}
      >
        SQ
      </button>
    </div>
  );
}

// Helper to get translated month name by number (1-12)
export function useMonthName() {
  const { t } = useTranslation();
  const getMonthName = (monthNum) => t(`months.${monthNum}`);
  const getMonthNameShort = (monthNum) => t(`months.short.${monthNum}`);
  return { getMonthName, getMonthNameShort };
}

const switcherStyles = {
  container: {
    display: 'inline-flex',
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  btn: {
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    background: '#f8fafc',
    color: '#64748b',
    transition: 'all 0.15s ease',
  },
  active: {
    background: '#0c4a6e',
    color: '#fff',
  },
};
